/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { getDocument, getWindow } from '../functions/index.js';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect.js';
import { useIsThemeAlreadySet } from './useIsThemeAlreadySet.js';
import '@itwin/itwinui-css/css/global.css';
import '@itwin/itwinui-variables/index.css';
/**
 * Hook that conditionally applies styling and theme to all components.
 * Defaults to light theme if none provided.
 *
 * If ThemeProvider is used as an ancestor, this hook will not do anything.
 *
 * @param theme Light, dark, or based on OS setting.
 * @param themeOptions Options that override default theming behavior.
 */
export const useTheme = (theme, themeOptions) => {
    var _a;
    const ownerDocument = (_a = themeOptions === null || themeOptions === void 0 ? void 0 : themeOptions.ownerDocument) !== null && _a !== void 0 ? _a : getDocument();
    const isThemeAlreadySet = useIsThemeAlreadySet(ownerDocument);
    useCorrectRootFontSize();
    useIsomorphicLayoutEffect(() => {
        if (!ownerDocument || isThemeAlreadySet.current) {
            return;
        }
        // do nothing if using v1 for the main page (incremental migration)
        if (ownerDocument.documentElement.className.includes('iui-theme-') ||
            ownerDocument.body.classList.contains('iui-body')) {
            return;
        }
        ownerDocument.body.classList.toggle('iui-root', true);
        switch (theme) {
            case 'light':
            case 'dark':
            case 'os': {
                return handleTheme(theme, ownerDocument, themeOptions === null || themeOptions === void 0 ? void 0 : themeOptions.highContrast);
            }
            default: {
                // set light theme by default
                if (ownerDocument.documentElement.dataset.iuiTheme == null) {
                    return handleTheme('light', ownerDocument, themeOptions === null || themeOptions === void 0 ? void 0 : themeOptions.highContrast);
                }
                return;
            }
        }
    }, [theme, themeOptions === null || themeOptions === void 0 ? void 0 : themeOptions.highContrast, ownerDocument]);
};
/**
 * Helper function to apply the specified theme, or detect the OS theme.
 * Returns a cleanup function to remove the change handlers.
 */
const handleTheme = (theme, ownerDocument, highContrast) => {
    var _a, _b, _c, _d, _e;
    const root = ownerDocument.documentElement;
    const _window = (_a = ownerDocument.defaultView) !== null && _a !== void 0 ? _a : getWindow();
    const applyThemeAttributes = (isDark = false, isHC = false) => {
        root.dataset.iuiTheme = isDark ? 'dark' : 'light';
        root.dataset.iuiContrast = isHC ? 'high' : 'default';
    };
    const prefersDarkQuery = (_b = _window === null || _window === void 0 ? void 0 : _window.matchMedia) === null || _b === void 0 ? void 0 : _b.call(_window, '(prefers-color-scheme: dark)');
    const prefersHCQuery = (_c = _window === null || _window === void 0 ? void 0 : _window.matchMedia) === null || _c === void 0 ? void 0 : _c.call(_window, '(prefers-contrast: more)');
    const changeHandler = () => {
        const isDark = theme === 'dark' || (theme === 'os' && (prefersDarkQuery === null || prefersDarkQuery === void 0 ? void 0 : prefersDarkQuery.matches));
        const isHC = highContrast !== null && highContrast !== void 0 ? highContrast : prefersHCQuery === null || prefersHCQuery === void 0 ? void 0 : prefersHCQuery.matches;
        applyThemeAttributes(isDark, isHC);
    };
    // call handler once to set initial theme
    changeHandler();
    // add listeners in supported browsers
    (_d = prefersDarkQuery === null || prefersDarkQuery === void 0 ? void 0 : prefersDarkQuery.addEventListener) === null || _d === void 0 ? void 0 : _d.call(prefersDarkQuery, 'change', changeHandler);
    (_e = prefersHCQuery === null || prefersHCQuery === void 0 ? void 0 : prefersHCQuery.addEventListener) === null || _e === void 0 ? void 0 : _e.call(prefersHCQuery, 'change', changeHandler);
    // return cleanup function to remove event listeners (should be returned from useEffect)
    return () => {
        var _a, _b;
        (_a = prefersDarkQuery === null || prefersDarkQuery === void 0 ? void 0 : prefersDarkQuery.removeEventListener) === null || _a === void 0 ? void 0 : _a.call(prefersDarkQuery, 'change', changeHandler);
        (_b = prefersHCQuery === null || prefersHCQuery === void 0 ? void 0 : prefersHCQuery.removeEventListener) === null || _b === void 0 ? void 0 : _b.call(prefersHCQuery, 'change', changeHandler);
    };
};
let didLogWarning = false;
let isDev = false;
// wrapping in try-catch because process might be undefined
try {
    isDev = process.env.NODE_ENV !== 'production';
}
catch (_a) { }
/** Shows console error if the page changes the root font size */
const useCorrectRootFontSize = () => {
    React.useEffect(() => {
        if (isDev && !didLogWarning) {
            const rootFontSize = parseInt(getComputedStyle(document.documentElement).fontSize);
            if (rootFontSize < 16) {
                console.error('Root font size must not be overridden. \nSee https://github.com/iTwin/iTwinUI/wiki/iTwinUI-react-v2-migration-guide#relative-font-size');
                didLogWarning = true;
            }
        }
    }, []);
};
